import React, { useEffect, useState } from "react";
import { Box, useMediaQuery, CircularProgress, Typography, IconButton } from "@mui/material";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import CustomButton from "../../../../components/CustomButton";
import mapicon from "../../../../assets/Dashboard/mapicon.svg";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { useTranslation } from "react-i18next";
import maparrow from "../../../../assets/maparrow.svg";
import CloseIcon from "@mui/icons-material/Close";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";
import {
  ATTENDANCE_HISTORY_MAP_LEGEND,
  attendanceHistoryStatusLabelKeyByValue,
  getAttendanceHistoryColorKey,
} from "./attendanceHistoryStatusOptions";
import {
  getGoogleMapsPinIconForStatus,
  getStatusBadgeBackgroundColor,
  resolvePunchPinStatusKey,
} from "../../../../constants/statusPalette";
const mapContainerStyle = { width: "100%", height: "100%" };

const AttendanceMap = ({ page, pageSize, status, startDate, endDate,
   selectedRow,
  onClearSelection,
    workerId,  
 }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:768px)");
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
const US_CENTER = { lat: 37.0902, lng: -95.7129 };
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const mapRef = React.useRef(null);
  const fullMapRef = React.useRef(null);
const normalizeAttendanceStatus = (text = "") =>
  text
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
const getAttendanceStatusLabel = (status) => {
  const statusKey = normalizeAttendanceStatus(status);
  const historyKey = attendanceHistoryStatusLabelKeyByValue[statusKey];
  if (historyKey) return t(historyKey);
  const translationMap = {
    ABSENT: "attendanceStatus.absent",
    LATE_ARRIVAL: "attendanceStatus.lateArrival",
    EARLY_OUT: "attendanceStatus.early_clock_out",
    LATE_AND_EARLY_OUT: "attendanceStatus.LATE_AND_EARLY_OUT",
    HAS_ISSUES: "attendanceStatus.unvalidPunch",
    HALF_DAY: "attendanceStatus.halfDay",
    OVERTIME: "attendanceStatus.overtime",
    PRESENT: "attendanceStatus.present",
  };
  return translationMap[statusKey] ? t(translationMap[statusKey]) : status;
};
const normalizeKey = (key) =>
  key.toLowerCase().replace(/_/g, "").replace(/-/g, "");

const formatTimeTo12Hour = (timeString) => {
  const localValue = convertUTCToLocalDateTime(timeString, {
    locale: "en-GB",
    formatOptions: { hour: "numeric", minute: "2-digit", hour12: true },
  });
  return localValue === "—" ? "--" : localValue;
};

const getDisplayPunchTime = (punch) => {
  if (punch?.occurredAt) return formatTimeTo12Hour(punch.occurredAt);
  if (punch?.time) return formatTimeTo12Hour(punch.time);
  return "--";
};




useEffect(() => {
  fitMapToMarkers(mapRef.current);
  if (isFullMapOpen) {
    fitMapToMarkers(fullMapRef.current);
  }
}, [markers, isFullMapOpen]);


const attendanceCounts = markers.reduce((acc, m) => {
  const rowStatus = m.legendStatus;
  if (rowStatus) {
    acc[rowStatus] = (acc[rowStatus] || 0) + 1;
  }
  const pinStatus = normalizeAttendanceStatus(m.status);
  if (pinStatus) {
    acc[pinStatus] = (acc[pinStatus] || 0) + 1;
  }
  return acc;
}, {});

const getLegendCount = (opt) => attendanceCounts[opt.value] || 0;


useEffect(() => {
  if (activeMarker) {
    const stillVisible = markers.some((marker) => marker.id === activeMarker.id);
    if (!stillVisible) setActiveMarker(null);
  }
}, [markers, activeMarker]);

 useEffect(() => {
  if (!decryptedToken) return;

  setLoading(true);

  let url = `${BASE_URL}/company-admins/attendance/history?pageSize=${pageSize}&page=${page}`;

  if (status) url += `&status=${status}`;
  if (startDate) url += `&dateFrom=${startDate}`;
  if (endDate) url += `&dateTo=${endDate}`;
  if (workerId) url += `&workerId=${workerId}`; 

  console.log("📍 MAP API URL:", url);

  fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${decryptedToken}`,
    },
  })
    .then((res) => res.json())
    .then((data) => {
      // const markers = [];

      // if (!data.error && data.data?.records) {
      //   data.data.records.forEach((rec) => {
      //     rec.punches?.forEach((p) => {
      //       if (p.location?.gpsLatitude && p.location?.gpsLongitude) {
      //         markers.push({
      //           id: p.id,
      //           lat: p.location.gpsLatitude,
      //           lng: p.location.gpsLongitude,
      //           worker: rec.worker.fullName,
      //           time: p.time,
      //           locationText: p.location.locationText || "Unknown",
      //           // status: rec.statusBadge?.text,
      //           status: normalizeAttendanceStatus(rec.statusBadge?.text),

      //         });
      //       }
      //     });
      //   });
      // }

      // setMarkers(markers);
     let markers = [];

if (selectedRow) {
  // ✅ ONLY SELECTED ROW
  selectedRow.punches?.forEach((p) => {
    if (p.location?.gpsLatitude && p.location?.gpsLongitude) {
      markers.push({
        id: p.id,
        lat: p.location.gpsLatitude,
        lng: p.location.gpsLongitude,
        worker: selectedRow.worker?.fullName || "",
        time: getDisplayPunchTime(p),
        locationText: p.location.locationText || "Unknown",
        status: resolvePunchPinStatusKey(p, selectedRow.statusBadge?.text),
        legendStatus: normalizeAttendanceStatus(selectedRow.statusBadge?.text),
      });
    }
  });
} else {
  // ✅ ALL ROWS
  if (!data.error && data.data?.records) {
    data.data.records.forEach((rec) => {
      rec.punches?.forEach((p) => {
        if (p.location?.gpsLatitude && p.location?.gpsLongitude) {
          markers.push({
            id: p.id,
            lat: p.location.gpsLatitude,
            lng: p.location.gpsLongitude,
            worker: rec.worker?.fullName || "",
            time: getDisplayPunchTime(p),
            locationText: p.location.locationText || "Unknown",
            status: resolvePunchPinStatusKey(p, rec.statusBadge?.text),
            legendStatus: normalizeAttendanceStatus(rec.statusBadge?.text),
          });
        }
      });
    });
  }
}

setMarkers(markers);
    })
    .catch((err) => {
      console.error("MAP FETCH ERROR:", err);
      setMarkers([]);
    })
    .finally(() => setLoading(false));
}, [  page,
  pageSize,
  status,
  startDate,
  endDate,
  decryptedToken,
    workerId,     // ✅

  selectedRow]);


  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
        <CircularProgress />
      </Box>
    );
  }


const getMapCenter = () =>
  markers.length > 0
    ? { lat: markers[0].lat, lng: markers[0].lng }
    : US_CENTER;

const getMarkerIcon = (status) => getGoogleMapsPinIconForStatus(status);

const fitMapToMarkers = (map) => {
  if (!map || !window.google?.maps) return;

  const validMarkers = markers.filter(
    (m) => typeof m.lat === "number" && typeof m.lng === "number"
  );

  if (!validMarkers.length) {
    map.setCenter(US_CENTER);
    map.setZoom(5);
    return;
  }

  if (validMarkers.length === 1) {
    map.setCenter({ lat: validMarkers[0].lat, lng: validMarkers[0].lng });
    map.setZoom(15);
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  validMarkers.forEach((marker) => {
    bounds.extend({ lat: marker.lat, lng: marker.lng });
  });
  map.fitBounds(bounds, 50);
};





const LegendItem = ({ color, label,count  }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        background: color,
        border: "1px solid #ccc",
      }}
    />
    <Typography sx={{ fontSize: "13px", color: "#333", fontFamily: "Poppins" }}>
      {label}({count || 0})
    </Typography>
  </Box>
);

  return (


    <Box sx={{mb:1, border: '1px solid #E4E6EF', borderRadius:2, p:2}}>

      <Box
  sx={{
    display: "flex",
    gap: 1,
    alignItems: "center",
    flexWrap: "wrap",
    mb: 1,
    px: 1,
  }}
>

  {ATTENDANCE_HISTORY_MAP_LEGEND.map((opt) => (
    <LegendItem
      key={opt.key}
      color={getStatusBadgeBackgroundColor(getAttendanceHistoryColorKey(opt.value))}
      label={t(opt.labelKey)}
      count={getLegendCount(opt)}
    />
  ))}

</Box>

    <Box sx={{ position: "relative", width: "100%", height: isMobile ? 300 : 400 }}>

      {/* Legend Section */}


      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={getMapCenter()}
        zoom={5}
        onLoad={(map) => {
          mapRef.current = map;
          fitMapToMarkers(map);
        }}
        onUnmount={() => {
          mapRef.current = null;
        }}
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
        }}
      >
        {markers.map((m) => (
          <Marker
            key={m.id}
            position={{ lat: m.lat, lng: m.lng }}
            icon={getMarkerIcon(m.status)}
            onClick={() => setActiveMarker(m)}
          />
        ))}
        {activeMarker && (
          <InfoWindow
            position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
            onCloseClick={() => setActiveMarker(null)}
          >
            <Box sx={{ minWidth: 150 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                {activeMarker.worker}
              </Typography>
              <Typography sx={{ fontSize: "13px" }}>{activeMarker.time}</Typography>
              <Typography sx={{ fontSize: "13px" }}>{activeMarker.locationText}</Typography>
              {/* <Typography sx={{ fontSize: "13px" }}>
                Status: {getAttendanceStatusLabel(activeMarker.status)}
              </Typography> */}
            </Box>
          </InfoWindow>
        )}
      </GoogleMap>
{selectedRow && (
  <IconButton
    onClick={onClearSelection}
    sx={{
      position: "absolute",
      top: 12,
      left: 12,
      zIndex: 1200,
      backgroundColor: "white",
      border: "1px solid #ddd",
      "&:hover": { backgroundColor: "#f5f5f5" }
    }}
  >
    <CloseIcon sx={{ color: "red" }} />
  </IconButton>
)}
      {/* Full Map Button */}
      <CustomButton
        onClick={() => setIsFullMapOpen(true)}
        size="small"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          backgroundColor: "#006EC2",
          color: "white",
          fontSize: "12px",
          padding: "4px 10px",
          borderRadius: "6px",
          zIndex: 1000, // always above the map
        }}
        label={<> {t("dashboard.viewFullMap")}
          <Box component="img" src={maparrow} alt="view icon" sx={{ ml: 1, width: 15, height: 13, objectFit: "contain" }} />
        </>}
      />

      {isFullMapOpen && (
        <Box sx={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 2000, backgroundColor: "white" }}>
          <IconButton
            onClick={() => setIsFullMapOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2100,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <CloseIcon sx={{ color: "red" }} />
          </IconButton>

          {/* Full map */}
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={getMapCenter()}
            zoom={5}
            onLoad={(map) => {
              fullMapRef.current = map;
              fitMapToMarkers(map);
            }}
            onUnmount={() => {
              fullMapRef.current = null;
            }}
            options={{
              fullscreenControl: false,
              mapTypeControl: false,
              streetViewControl: false,
            }}
          >
            {markers.map((m) => (
              <Marker
                key={m.id}
                position={{ lat: m.lat, lng: m.lng }}
                icon={getMarkerIcon(m.status)}
                onClick={() => setActiveMarker(m)}
              />
            ))}
            {activeMarker && (
              <InfoWindow
                position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
                onCloseClick={() => setActiveMarker(null)}
              >
                <Box sx={{ minWidth: 150 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                    {activeMarker.worker}
                  </Typography>
                  <Typography sx={{ fontSize: "13px" }}>{activeMarker.time}</Typography>
                  <Typography sx={{ fontSize: "13px" }}>{activeMarker.locationText}</Typography>
                  <Typography sx={{ fontSize: "13px" }}>
                    Status: {getAttendanceStatusLabel(activeMarker.status)}
                  </Typography>
                </Box>
              </InfoWindow>
            )}
          </GoogleMap>

        </Box>
      )}
    </Box>
    
          {(status || startDate || endDate) && (
  <Box
    sx={{
      mt: 1,
      mb: 1,
      px: 2,
      py: 1,
      borderRadius: 2,
      backgroundColor: "#F5F6FA",
      border: "1px solid #E4E6EF",
      fontFamily: "Poppins",
      display: "flex",
      flexDirection: "column",
      gap: "4px"
    }}
  >
    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#003149" ,      fontFamily: 'Poppins, sans-serif',
}}>
      {t("mapTab.appliedFilters")}:
    </Typography>

    {/* Status */}
    {status && (
      <Typography sx={{ fontSize: "13px", color: "#333",      fontFamily: 'Poppins, sans-serif',
 }}>
        {t("mapTab.attendanceStatus.status")}:{" "}
    <b>{getAttendanceStatusLabel(status)}</b>

      </Typography>
    )}

    {/* Date From */}
    {startDate && (
      <Typography sx={{ fontSize: "13px", color: "#333",      fontFamily: 'Poppins, sans-serif',
 }}>
        {t("mapTab.filters.from")}: <b>{startDate}</b>
      </Typography>
    )}

    {/* Date To */}
    {endDate && (
      <Typography sx={{ fontSize: "13px", color: "#333",      fontFamily: 'Poppins, sans-serif',
 }}>
        {t("mapTab.filters.to")}: <b>{endDate}</b>
      </Typography>
    )}
  </Box>
)}
    </Box>
  );
};

export default AttendanceMap;



